import type { AccessRequestItemDto } from "@/services/UserIdentitiesService";
import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";
import { AccessRequestCurrentRow, AccessRequestHistoryRow, AccessRequestOperationType } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";

// Estados tal y como los serializa el backend (UserIdentityRequestStatusEnum). Las dos familias van
// separadas a propósito: un préstamo concede acceso, una cesión cambia al propietario, y el usuario tiene
// que poder distinguirlas de un vistazo en la misma tabla.
const ACCESS_REQUEST_STATUS_LABELS: Record<string, string> = {
    Pending: "Pendiente",
    Approved: "Aprobada",
    Rejected: "Rechazada",
    Revoked: "Revocada",
    OwnershipTransferPending: "Cesión pendiente",
    OwnershipTransferApproved: "Cesión aprobada",
    OwnershipTransferAuto: "Cesión automática",
    // Decayó porque otra solicitud sobre el mismo interviniente se aprobó antes. No es un rechazo: nadie la
    // rechazó, se quedó sin objeto, y el texto lo dice para que el solicitante sepa qué pasó de verdad.
    OwnershipTransferSuperseded: "Cedido a otro usuario",
};

export function formatAccessRequestStatusForDisplay(status: string | undefined | null): string
{
    if(status === undefined || status === null || status === "")
    {
        return "—";
    }
    return ACCESS_REQUEST_STATUS_LABELS[status] ?? status;
}

/**
 * Estados en los que la solicitud sigue esperando la decisión del propietario, y que por tanto tienen que
 * salir con botones de aceptar/rechazar. La cesión entra aquí igual que el préstamo: se resuelve por el
 * mismo endpoint y con la misma firma.
 */
const UNRESOLVED_ACCESS_REQUEST_STATUSES = ["Pending", "OwnershipTransferPending"];

export function isUnresolvedAccessRequestStatus(statusCode: string | undefined | null): boolean
{
    return statusCode !== undefined && statusCode !== null && UNRESOLVED_ACCESS_REQUEST_STATUSES.includes(statusCode);
}

/**
 * Cómo acaba el intento de resolver una solicitud ya firmada:
 * - "ok": resuelta.
 * - "retryable": falló la red o el servidor, no la firma. Se puede reintentar con la MISMA firma sin
 *   riesgo, porque el backend es idempotente con la firma como clave (si la primera llamada llegó a
 *   grabar, el reintento responde éxito).
 * - "final": la firma ya no sirve o la solicitud ya no admite esta decisión. Reintentar con la misma
 *   firma no puede salir bien nunca: ofrecerlo era lo que dejaba al usuario en bucle.
 */
export type AccessRequestDecisionOutcome = "ok" | "retryable" | "final";

/**
 * Únicos errores que se arreglan reintentando la misma decisión: lista blanca a propósito. Cualquier otro
 * código (firma caducada, ya resuelta, ya no eres el propietario, respuesta sin envoltorio de un 403...)
 * es definitivo, y ante la duda es mejor cerrar la ceremonia que ofrecer un reintento que no puede
 * funcionar.
 */
const TRANSIENT_DECISION_ERROR_CODES = ["NETWORK_ERROR", "INTEGRATION_SERVICE_UNAVAILABLE", "INTERVENER_OPERATION_FAILED"];

export function classifyAccessRequestDecisionFailure(errors: {code?: string | null}[] | null | undefined): AccessRequestDecisionOutcome
{
    if(!errors || errors.length === 0)
    {
        return "final";
    }
    return errors.every((e) => e.code != null && TRANSIENT_DECISION_ERROR_CODES.includes(e.code)) ? "retryable" : "final";
}

/**
 * Estado en el que queda una solicitud aprobada, para pintar la fila sin esperar a recargar. Refleja lo que
 * hace el backend: una cesión aprobada no acaba en «Approved», porque ese estado significa acceso compartido
 * vigente y el nuevo propietario no es un invitado sobre su propio interviniente.
 */
export function resolveApprovedStatusCode(statusCode: string | undefined | null): string
{
    return statusCode === "OwnershipTransferPending" ? "OwnershipTransferApproved" : "Approved";
}

const mapOperation = (item: AccessRequestItemDto): AccessRequestOperationType =>
{
    const opRaw = item.operation;
    if(opRaw === "Cesión" || opRaw === "Cesion")
    {
        return "Cesión";
    }
    return "Autorización";
};

export const mapAccessRequestItemToCurrentRow = (item: AccessRequestItemDto): AccessRequestCurrentRow =>
{
    const name = `${item.userIdentityFirstName ?? ""} ${item.userIdentityLastName ?? ""}`.trim();
    const approvalFormatted = formatIsoDateTimeToDdMmYyyy(item.approvalDate);

    const statusCode = String(item.status ?? "");

    return{
        id: item.accessRequestId,
        userIdentityId: item.userIdentityId,
        guestEmail: String(item.guestEmail ?? ""),
        identityName: name || "—",
        operation: mapOperation(item),
        requestDate: formatIsoDateTimeToDdMmYyyy(item.createdDate),
        ...(approvalFormatted !== "—" ? { approvalDate: approvalFormatted } : {}),
        statusCode,
        status: formatAccessRequestStatusForDisplay(item.status),
    };
};

export const mapAccessRequestItemToHistoryRow = (item: AccessRequestItemDto): AccessRequestHistoryRow =>
{
    const name = `${item.userIdentityFirstName ?? ""} ${item.userIdentityLastName ?? ""}`.trim();

    return{
        id: item.accessRequestId,
        guestEmail: String(item.guestEmail ?? ""),
        identityName: name || "—",
        operation: mapOperation(item),
        requestDate: formatIsoDateTimeToDdMmYyyy(item.createdDate),
        resolutionDate: formatIsoDateTimeToDdMmYyyy(item.approvalDate),
        status: formatAccessRequestStatusForDisplay(item.status),
    };
};

export const currentRowToHistoryRow = (row: AccessRequestCurrentRow): AccessRequestHistoryRow =>
{
    const resolution = row.approvalDate && row.approvalDate !== "—" ? row.approvalDate : "—";

    return{
        id: row.id,
        guestEmail: row.guestEmail,
        identityName: row.identityName,
        operation: row.operation,
        requestDate: row.requestDate,
        resolutionDate: resolution,
        status: row.status,
    };
};